import { useEffect, useState } from 'react';
import { CheckCircle2, Circle, Plus, Save, Send, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { Card } from '@/components/ui/Card';
import { callBackend, useMutation, useQuery } from '@/lib/api-hooks';
import { backendApi } from '@/lib/backend';
import { CRM_FEATURE_DEFINITIONS } from '../../../convex/crmFeatures';
import { useCrm } from './CrmContext';
import { PopupDelivery } from './PopupDelivery';
import { CrmFeaturesEditor } from '@/components/admin/CrmFeaturesEditor';
import { pushSupported } from '@/lib/push';
import { ExtensionPicker, Field, Toggle } from './fields';
import { buttonClass, errorMessage, formatWhen, inputClass } from './format';
import type { CustomFieldDefinition, ScreenPopMode } from './types';

function MySettings() {
  const { data, hasFeature, liveConnected } = useCrm();
  const savePrefs = useMutation(backendApi.crmSettings.saveMyPrefs);
  const [extension, setExtension] = useState(data?.prefs.extension ?? '');
  const [mode, setMode] = useState<ScreenPopMode>(data?.prefs.screenPopMode ?? 'mine');
  const [desktop, setDesktop] = useState(data?.prefs.desktopNotifications ?? false);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      if (desktop && 'Notification' in window && Notification.permission === 'default') await Notification.requestPermission();
      await savePrefs({ extension, screenPopMode: mode, desktopNotifications: desktop });
      toast.success('Saved');
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card animate={false}>
      <h3 className="font-semibold text-gray-900 dark:text-white mb-1">My settings</h3>
      <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">Just for you, on this account.</p>
      <div className="space-y-4 max-w-xl">
        <Field label="My extension" hint="Click to call rings this phone first. Callbacks assigned to it show as yours.">
          <ExtensionPicker value={extension} onChange={(value) => setExtension(value)} placeholder="Not set" />
        </Field>
        {hasFeature('screenPop') && (
          <>
            <Field label="Incoming call pop-up">
              <select value={mode} onChange={(event) => setMode(event.target.value as ScreenPopMode)} className={inputClass}>
                <option value="mine">When my extension rings</option>
                <option value="all">For every incoming call</option>
                <option value="off">Off</option>
              </select>
            </Field>
            {!(data?.pushPublicKey && pushSupported()) && (
              <Toggle
                checked={desktop}
                onChange={setDesktop}
                label="Desktop notifications"
                description="Also notify through the browser when Yeastarify is in a background tab."
              />
            )}
            <p className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
              {liveConnected ? <CheckCircle2 className="w-3.5 h-3.5 text-green-600" /> : <Circle className="w-3.5 h-3.5" />}
              {liveConnected ? 'Live connection to the server is up.' : 'Live connection is not up; pop-ups appear once it reconnects.'}
            </p>
          </>
        )}
        <button type="button" onClick={() => void save()} disabled={saving} className={buttonClass.primary}>
          <Save className="w-4 h-4" /> Save
        </button>
      </div>
    </Card>
  );
}

function AccountSettings() {
  const { data, hasFeature } = useCrm();
  const saveSettings = useMutation(backendApi.crmSettings.saveSettings);
  const settings = data!.settings;
  const [form, setForm] = useState({
    callbackSlaMinutes: settings.callbackSlaMinutes,
    vipSlaMinutes: settings.vipSlaMinutes,
    customFields: settings.customFields as CustomFieldDefinition[],
    digestRecipients: settings.digestRecipients.join(', '),
    webhookUrl: settings.webhookUrl,
    webhookSecret: undefined as string | undefined,
    aiConsentConfirmed: Boolean(settings.aiConsentConfirmedAt),
    aiMinTalkSeconds: settings.aiMinTalkSeconds,
    aiDailyLimit: settings.aiDailyLimit,
    transcriptRetentionDays: settings.transcriptRetentionDays,
    autoAssignOwner: settings.autoAssignOwner,
    lookupDailyLimit: settings.lookupDailyLimit,
  });
  const lookupUsage = useQuery<{ usedToday: number; dailyLimit: number; google: boolean; web: boolean }>(
    backendApi.crmLookup.getUsage,
    hasFeature('callerLookup') ? {} : 'skip'
  );
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    setForm((current) => ({ ...current, webhookSecret: undefined }));
  }, [settings.webhookSecretSet]);

  const save = async () => {
    setSaving(true);
    try {
      await saveSettings({
        ...form,
        digestRecipients: form.digestRecipients.split(/[,;\s]+/).filter(Boolean),
        customFields: form.customFields.map((field) => ({ ...field, options: field.type === 'select' ? field.options : undefined })),
      });
      toast.success('CRM settings saved');
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  const testWebhook = async () => {
    setTesting(true);
    try {
      const result = await callBackend<{ ok: boolean; status: string }>(backendApi.crmIntegrations.sendTestWebhook, {});
      if (result.ok) toast.success(`Webhook answered: ${result.status}`);
      else toast.error(`Webhook: ${result.status}`);
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setTesting(false);
    }
  };

  const number = (value: string) => Number(value) || 0;

  return (
    <Card animate={false}>
      <h3 className="font-semibold text-gray-900 dark:text-white mb-1">CRM settings</h3>
      <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">For everyone on this account.</p>
      <div className="space-y-8 max-w-2xl">
        {hasFeature('missedCallRecovery') && (
          <section className="space-y-3">
            <h4 className="text-sm font-semibold text-gray-900 dark:text-white">Callback deadlines</h4>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Missed calls (minutes)">
                <input type="number" min={5} value={form.callbackSlaMinutes} onChange={(event) => setForm({ ...form, callbackSlaMinutes: number(event.target.value) })} className={inputClass} />
              </Field>
              <Field label="VIP missed calls (minutes)">
                <input type="number" min={1} value={form.vipSlaMinutes} onChange={(event) => setForm({ ...form, vipSlaMinutes: number(event.target.value) })} className={inputClass} />
              </Field>
            </div>
          </section>
        )}

        <section className="space-y-3">
          <h4 className="text-sm font-semibold text-gray-900 dark:text-white">Custom contact fields</h4>
          <p className="text-xs text-gray-500 dark:text-gray-400">Extra fields on every contact, e.g. account number, branch or contract renewal date.</p>
          {form.customFields.map((field, index) => (
            <div key={index} className="flex flex-wrap gap-2 items-start">
              <input
                value={field.label}
                onChange={(event) => setForm({ ...form, customFields: form.customFields.map((item, i) => (i === index ? { ...item, label: event.target.value } : item)) })}
                placeholder="Label"
                className={`${inputClass} flex-1 min-w-[10rem]`}
              />
              <select
                value={field.type}
                onChange={(event) =>
                  setForm({ ...form, customFields: form.customFields.map((item, i) => (i === index ? { ...item, type: event.target.value as CustomFieldDefinition['type'] } : item)) })
                }
                className={`${inputClass} w-32`}
              >
                <option value="text">Text</option>
                <option value="number">Number</option>
                <option value="date">Date</option>
                <option value="select">Choice</option>
              </select>
              {field.type === 'select' && (
                <input
                  value={(field.options ?? []).join(', ')}
                  onChange={(event) =>
                    setForm({ ...form, customFields: form.customFields.map((item, i) => (i === index ? { ...item, options: event.target.value.split(',').map((option) => option.trim()) } : item)) })
                  }
                  placeholder="Options, comma separated"
                  className={`${inputClass} flex-1 min-w-[12rem]`}
                />
              )}
              <button type="button" onClick={() => setForm({ ...form, customFields: form.customFields.filter((_, i) => i !== index) })} className="p-2 text-gray-400 hover:text-red-600" aria-label="Remove field">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
          {form.customFields.length < 20 && (
            <button type="button" onClick={() => setForm({ ...form, customFields: [...form.customFields, { key: '', label: '', type: 'text' }] })} className="inline-flex items-center gap-1 text-sm text-primary-600 dark:text-primary-400 hover:underline">
              <Plus className="w-4 h-4" /> Add field
            </button>
          )}
        </section>

        {hasFeature('insights') && (
          <section>
            <Toggle
              checked={form.autoAssignOwner}
              onChange={(autoAssignOwner) => setForm({ ...form, autoAssignOwner })}
              label="Assign account owners automatically"
              description="When one extension handles most of a customer's calls, make it the owner without asking."
            />
          </section>
        )}

        {hasFeature('weeklyDigest') && (
          <section className="space-y-2">
            <h4 className="text-sm font-semibold text-gray-900 dark:text-white">Weekly summary</h4>
            <Field label="Send to" hint="Email addresses, comma separated. Sent Monday mornings.">
              <input value={form.digestRecipients} onChange={(event) => setForm({ ...form, digestRecipients: event.target.value })} className={inputClass} placeholder="owner@company.co.za" />
            </Field>
            {!data!.capabilities.email && <p className="text-xs text-amber-700 dark:text-amber-400">Email sending is not set up on the server yet, so nothing is sent.</p>}
          </section>
        )}

        {hasFeature('webhooks') && (
          <section className="space-y-2">
            <h4 className="text-sm font-semibold text-gray-900 dark:text-white">Webhook</h4>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Calls, missed calls, new and changed contacts, closed tasks and notes are POSTed as JSON, e.g. to a Zapier or Make "catch hook" that updates HubSpot,
              Pipedrive or Zoho. With a secret, each request carries an X-Yeastarify-Signature (HMAC-SHA256 of timestamp.body).
            </p>
            <Field label="URL">
              <input value={form.webhookUrl} onChange={(event) => setForm({ ...form, webhookUrl: event.target.value })} className={inputClass} placeholder="https://hooks.zapier.com/…" />
            </Field>
            <Field label="Secret" hint={settings.webhookSecretSet ? 'A secret is set. Type a new one to replace it, or clear the box and save to remove it.' : 'Optional.'}>
              <input
                type="password"
                value={form.webhookSecret ?? ''}
                onChange={(event) => setForm({ ...form, webhookSecret: event.target.value })}
                className={inputClass}
                placeholder={settings.webhookSecretSet ? '••••••••' : ''}
                autoComplete="new-password"
              />
            </Field>
            <div className="flex items-center gap-3">
              <button type="button" onClick={() => void testWebhook()} disabled={testing || !settings.webhookUrl} className={buttonClass.secondary}>
                <Send className="w-4 h-4" /> Send a test
              </button>
              {settings.webhookLastStatus && (
                <span className="text-xs text-gray-500">
                  Last: {settings.webhookLastStatus} {settings.webhookLastAtMs ? `(${formatWhen(settings.webhookLastAtMs)})` : ''}
                </span>
              )}
            </div>
          </section>
        )}

        {hasFeature('callerLookup') && (
          <section className="space-y-3">
            <h4 className="text-sm font-semibold text-gray-900 dark:text-white">Unknown caller look-up</h4>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Numbers that are not contacts yet are looked up while they ring, and new callers every few minutes. Only the phone number is sent, to{' '}
              {[data!.capabilities.lookupGoogle && 'Google Maps', data!.capabilities.lookupWeb && 'a web search service (Perplexity)'].filter(Boolean).join(' and ') ||
                'the look-up services'}
              . Suggested names are never saved until someone on your team accepts them.
            </p>
            {!data!.capabilities.lookup && <p className="text-xs text-amber-700 dark:text-amber-400">Caller look-up is not set up on the server yet.</p>}
            {data!.capabilities.lookup && !data!.capabilities.lookupGoogle && (
              <p className="text-xs text-gray-500 dark:text-gray-400">Google Maps is not set up on the server yet; web search only.</p>
            )}
            <div className="grid grid-cols-2 gap-4 items-end">
              <Field label="Most look-ups per day">
                <input type="number" min={1} max={1000} value={form.lookupDailyLimit} onChange={(event) => setForm({ ...form, lookupDailyLimit: number(event.target.value) })} className={inputClass} />
              </Field>
              {lookupUsage && (
                <p className="text-sm text-gray-600 dark:text-gray-300 pb-2">
                  {lookupUsage.usedToday} used in the last 24 hours
                </p>
              )}
            </div>
          </section>
        )}

        {hasFeature('aiCallSummaries') && (
          <section className="space-y-3">
            <h4 className="text-sm font-semibold text-gray-900 dark:text-white">AI call summaries</h4>
            {!data!.capabilities.transcription && <p className="text-xs text-amber-700 dark:text-amber-400">Transcription is not set up on the server yet.</p>}
            <Toggle
              checked={form.aiConsentConfirmed}
              onChange={(aiConsentConfirmed) => setForm({ ...form, aiConsentConfirmed })}
              label="Callers are told calls are recorded, and we may process recordings"
              description={
                settings.aiConsentConfirmedAt
                  ? `Confirmed by ${settings.aiConsentConfirmedBy} on ${new Date(settings.aiConsentConfirmedAt).toLocaleDateString()}. Summaries only run while this is on.`
                  : 'Required (POPIA). Recordings are sent to the transcription service; transcripts are kept for the retention period below.'
              }
            />
            <div className="grid grid-cols-3 gap-4">
              <Field label="Shortest call (seconds)">
                <input type="number" value={form.aiMinTalkSeconds} onChange={(event) => setForm({ ...form, aiMinTalkSeconds: number(event.target.value) })} className={inputClass} />
              </Field>
              <Field label="Most per day">
                <input type="number" value={form.aiDailyLimit} onChange={(event) => setForm({ ...form, aiDailyLimit: number(event.target.value) })} className={inputClass} />
              </Field>
              <Field label="Keep transcripts (days)">
                <input type="number" value={form.transcriptRetentionDays} onChange={(event) => setForm({ ...form, transcriptRetentionDays: number(event.target.value) })} className={inputClass} />
              </Field>
            </div>
          </section>
        )}

        <button type="button" onClick={() => void save()} disabled={saving} className={buttonClass.primary}>
          <Save className="w-4 h-4" /> {saving ? 'Saving…' : 'Save CRM settings'}
        </button>
      </div>
    </Card>
  );
}

function FeatureList() {
  const { data } = useCrm();
  // Admins get the switches here as well as in the Admin Console.
  if (data?.isAdmin) return <CrmFeaturesEditor customerId={data.customerId} customerName="" />;
  return (
    <Card animate={false}>
      <h3 className="font-semibold text-gray-900 dark:text-white mb-1">Features on this account</h3>
      <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">Switched on and off by InspireTel. Ask us about anything that is off.</p>
      <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {CRM_FEATURE_DEFINITIONS.filter((feature) => !feature.internal).map((feature) => {
          const on = Boolean(data?.features[feature.id]);
          return (
            <li key={feature.id} className="flex gap-2">
              {on ? <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0 text-green-600" /> : <Circle className="w-4 h-4 mt-0.5 shrink-0 text-gray-400" />}
              <span>
                <span className={`block text-sm font-medium ${on ? 'text-gray-900 dark:text-white' : 'text-gray-500 dark:text-gray-400'}`}>
                  {feature.label} <span className="font-normal text-xs">{on ? 'On' : 'Off'}</span>
                </span>
                <span className="block text-xs text-gray-500 dark:text-gray-400">{feature.description}</span>
              </span>
            </li>
          );
        })}
      </ul>
    </Card>
  );
}

export function SettingsTab() {
  const { data } = useCrm();
  return (
    <div className="space-y-6">
      <MySettings />
      <PopupDelivery />
      {data?.canManage && <AccountSettings />}
      <FeatureList />
    </div>
  );
}
